
/**
 * Utility functions for parsing CSV data
 */

/**
 * Parse CSV text into an array of string arrays
 * Handles quoted fields with commas inside properly
 * Includes error handling for common malformed CSV issues
 */
export function parseCSV(text: string): string[][] {
  if (!text || typeof text !== 'string') {
    throw new Error("Invalid CSV: Input must be a non-empty string");
  }

  try {
    // Split by line breaks, filtering out empty lines
    const lines = text.split('\n').filter(line => line.trim().length > 0);
    
    if (lines.length === 0) {
      throw new Error("Empty CSV: No data rows found");
    }
    
    return lines.map((line, lineIndex) => {
      // Handle quoted fields with commas inside properly
      const result = [];
      let field = '';
      let inQuotes = false;
      let unclosedQuote = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
          // Handle escaped quotes (double quotes)
          if (nextChar === '"') {
            field += '"';
            i++; // Skip the next quote
          } else {
            inQuotes = !inQuotes;
            // Track if we have an unclosed quote
            if (inQuotes) {
              unclosedQuote = true;
            } else {
              unclosedQuote = false;
            }
          }
        } else if (char === ',' && !inQuotes) {
          result.push(field);
          field = '';
        } else {
          field += char;
        }
      }
      
      // Check for unclosed quotes at the end of processing
      if (unclosedQuote) {
        console.warn(`Warning: Unclosed quote in CSV at line ${lineIndex + 1}`);
      }
      
      // Push the last field
      result.push(field);
      
      // Check if there's at least some data in the row
      if (result.every(cell => cell.trim() === '')) {
        console.warn(`Warning: Empty row detected at line ${lineIndex + 1}`);
      }
      
      return result;
    });
  } catch (error) {
    console.error("Error parsing CSV:", error);
    throw new Error(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validates if CSV data contains the expected minimum structure
 * Returns true if valid, throws error with details if not
 */
export function validateCSVStructure(parsedData: string[][]): boolean {
  if (!Array.isArray(parsedData) || parsedData.length === 0) {
    throw new Error("Invalid CSV structure: No data rows found");
  }
  
  if (!Array.isArray(parsedData[0]) || parsedData[0].length === 0) {
    throw new Error("Invalid CSV structure: Header row is empty or malformed");
  }
  
  // Check if all rows have the same number of columns
  const headerLength = parsedData[0].length;
  const unevenRow = parsedData.findIndex(row => row.length !== headerLength);
  
  if (unevenRow !== -1) {
    throw new Error(`Malformed CSV: Row ${unevenRow + 1} has ${parsedData[unevenRow].length} columns instead of ${headerLength}`);
  }
  
  return true;
}

